import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import { Modal } from 'antd';
import { format } from 'date-fns';

import WarningIcon from 'assets/warning_outlined.svg?react';

import FormSection from 'components/FormSection';
import SectionWrapper from 'components/SectionWrapper';
import FormToggle from 'components/FormToggle';
import LoadingSpinner from 'components/LoadingSpinner';
import InfoBanner from 'components/InfoBanner';
import {
  FormValues,
  WeatherData,
  WeatherMode
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
// eslint-disable-next-line import/no-unresolved
import { useAgOperationUtility } from '../../Context/AgOperationUtilityProvider';

import WeatherSection from './components/WeatherSection';
import useWeatherBanner from './hooks/useWeatherBanner';
import { WeatherErrorType } from '../../Helpers/weatherErrorModal';
import './index.css';

const WeatherStep = () => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const {
    utilityState,
    setAutoWeatherEnabled,
    setHasModifiedWeatherData,
    fetchWeatherData,
    isWeatherLoading,
    originalWeatherData,
    resetOriginalWeatherData,
    areDatesInFuture,
    setHasVisitedWeatherTab,
    apiErrorValue
  } = useAgOperationUtility();

  // Mark that user has visited the weather tab (used to control edit mode modal)
  useEffect(() => {
    setHasVisitedWeatherTab(true);
  }, [setHasVisitedWeatherTab]);

  const { autoWeatherEnabled, hasModifiedWeatherData } = utilityState;

  const startHasModifiedWeatherData = useRef<boolean>(hasModifiedWeatherData);

  const endHasModifiedWeatherData = useRef<boolean>(hasModifiedWeatherData);

  // Reset trigger for WeatherSection components
  const [resetTrigger, setResetTrigger] = useState(0);

  const lastWeatherMode = React.useRef<WeatherMode | null>(
    values.weather?.mode || null
  );

  // Weather preferences are now loaded in the context provider on mount
  // No need to load them here - just use the value from context
  useEffect(() => {
    if (values.weather?.mode === WeatherMode.AUTOMATIC) {
      // Capture current form weather values BEFORE fetching
      // This allows the fetch to merge API data with user's modified fields
      const currentWeatherData = {
        start: values.weather?.start,
        end: values.weather?.end
      };

      // Clear stored original values before fetching new data
      resetOriginalWeatherData();
      // Pass current form values so they can be merged with API response
      fetchWeatherData(currentWeatherData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.weather?.mode]);

  // Extract dates from Formik values for displaying weather sections
  const startDate = (values as any)?.startDateTime || null;

  const endDate = (values as any)?.endDateTime || null;

  // Format titles with dates
  const startWeatherTitle = startDate
    ? `START WEATHER: ${format(
        startDate.toDate(),
        'MMM dd, yyyy hh:mma'
      )}`.toUpperCase()
    : 'START WEATHER';

  const endWeatherTitle = endDate
    ? `END WEATHER: ${format(
        endDate.toDate(),
        'MMM dd, yyyy hh:mma'
      )}`.toUpperCase()
    : 'END WEATHER';

  // Handle toggle changes with reset functionality
  const handleToggleChange = (enabled: boolean) => {
    // If turning ON from OFF, reset everything
    // The useEffect watching values.weather?.mode will call fetchWeatherData()
    if (enabled) {
      // Reset the modification state
      setHasModifiedWeatherData(false);

      // Trigger reset in WeatherSection components
      setResetTrigger(prev => prev + 1);
    }

    // If turning OFF from ON, clear unmodified fields but keep modified ones
    if (!enabled) {
      // Clear the global modification state when turning OFF
      setHasModifiedWeatherData(false);

      const currentWeatherData = (values as any)?.weather || {};

      // Helper function to clear weather fields for a specific period (start/end)
      // Only keeps fields that are in manuallyModifiedFields
      const clearWeatherFieldsForPeriod = (period: 'start' | 'end') => {
        const currentPeriodData = currentWeatherData[period];
        const modifiedFields = currentPeriodData?.manuallyModifiedFields || [];

        const weatherFields: (keyof Omit<
          WeatherData,
          'manuallyModifiedFields'
        >)[] = [
          'skyCondition',
          'temperature',
          'humidity',
          'windSpeed',
          'windDirection',
          'soilMoisture'
        ];

        // Identify numeric fields that should be cleared to null instead of empty string
        const numericFields = new Set(['temperature', 'humidity', 'windSpeed']);

        weatherFields.forEach(field => {
          // Only clear if field is NOT in manuallyModifiedFields
          if (!modifiedFields.includes(field)) {
            const isNumericField = numericFields.has(field);
            // For numeric fields, use null to prevent FormInputWithPrecisionDisplay from showing "0.00"
            setFieldValue(
              `weather.${period}.${field}`,
              isNumericField ? null : ''
            );
          }
          // If field was manually modified, leave it as is (keep user's changes)
        });
      };

      // Clear fields for both start and end weather
      clearWeatherFieldsForPeriod('start');
      clearWeatherFieldsForPeriod('end');

      // Force trigger reset to clear warnings in WeatherSection components
      setResetTrigger(prev => prev + 1);
    }

    lastWeatherMode.current = values.weather?.mode || null;

    setFieldValue(
      'weather.mode',
      enabled ? WeatherMode.AUTOMATIC : WeatherMode.MANUAL
    );
    // Update the toggle state
    setAutoWeatherEnabled(enabled);
  };

  const setHasModifiedWeatherDataEvent = (
    modified: boolean,
    period: 'start' | 'end'
  ) => {
    if (period === 'start') {
      startHasModifiedWeatherData.current = modified;
    } else if (period === 'end') {
      endHasModifiedWeatherData.current = modified;
    }

    if (
      modified ||
      (period === 'start'
        ? endHasModifiedWeatherData.current
        : startHasModifiedWeatherData.current)
    ) {
      setHasModifiedWeatherData(true);
    } else {
      setHasModifiedWeatherData(false);
    }
  };

  const isTheToggleChecked = useMemo(() => {
    return values.weather?.mode
      ? values.weather.mode === WeatherMode.AUTOMATIC
      : autoWeatherEnabled;
  }, [autoWeatherEnabled, values.weather?.mode]);

  const renderToggle = () => (
    <FormToggle
      checked={isTheToggleChecked}
      onChange={handleToggleChange}
      toggleLabel="Get weather automatically"
      checkedChildren="on"
      unCheckedChildren="off"
    />
  );

  // Get banner configuration from hook
  const {
    showBanner,
    icon: BannerIcon,
    iconColor,
    backgroundColor,
    borderColor,
    textColor,
    message
  } = useWeatherBanner({
    areDatesInFuture: areDatesInFuture(),
    isAutoWeatherEnabled: values.weather?.mode === WeatherMode.AUTOMATIC,
    apiErrorValue: apiErrorValue as WeatherErrorType | null
  });

  return (
    <div className="weather-step-container relative flex flex-col w-full pb-4">
      {/* Weather loading modal */}
      <Modal
        open={isWeatherLoading}
        footer={null}
        closable={false}
        centered
        width={400}
        maskClosable={false}
        keyboard={false}
        destroyOnClose={false}
        zIndex={1000}
        transitionName=""
        maskTransitionName=""
      >
        <LoadingSpinner message="Loading weather data..." />
      </Modal>

      {!isWeatherLoading && (
        <>
          {/* Global Weather Warning Banner */}
          {!areDatesInFuture() && !apiErrorValue && hasModifiedWeatherData && (
            <div
              className="absolute top-4 right-0 z-10 flex items-center gap-2"
              style={{
                width: '290px',
                height: '32px',
                padding: '12px 16px',
                borderRadius: '4px',
                border: '1px solid var(--Yellow-yellow-30, #F0C355)',
                background: 'var(--Yellow-yellow-10, #FFF3DD)',
                whiteSpace: 'nowrap'
              }}
            >
              <WarningIcon
                width={24}
                height={24}
                style={{ color: '#C17E19' }}
              />
              <span className="text-sm font-medium text-neutral-1000">
                Automatic Weather Data Modified
              </span>
            </div>
          )}

          <SectionWrapper isFirst>
            <FormSection
              title={startWeatherTitle}
              autoHeight
              toggleContent={renderToggle()}
              showBanner={showBanner}
              bannerContent={
                showBanner &&
                BannerIcon && (
                  <InfoBanner
                    icon={
                      <BannerIcon
                        width={24}
                        height={24}
                        style={{ color: iconColor }}
                      />
                    }
                    backgroundColor={backgroundColor}
                    borderColor={borderColor}
                    textColor={textColor}
                  >
                    {message}
                  </InfoBanner>
                )
              }
            >
              <div
                className="weather-step-form"
                data-testid="weather-step-form-start"
              >
                <WeatherSection
                  prefix="start"
                  originalValues={originalWeatherData?.start}
                  setHasModifiedWeatherData={modified =>
                    setHasModifiedWeatherDataEvent(modified, 'start')
                  }
                  resetTrigger={resetTrigger}
                  autoWeatherEnabled={autoWeatherEnabled}
                  lastWeatherMode={lastWeatherMode}
                  areDatesInFuture={areDatesInFuture()}
                  apiErrorValue={apiErrorValue}
                />
              </div>
            </FormSection>
          </SectionWrapper>

          <SectionWrapper>
            <FormSection title={endWeatherTitle} hasMarginBottom={false}>
              <div
                className="weather-step-form"
                data-testid="weather-step-form-end"
              >
                <WeatherSection
                  prefix="end"
                  originalValues={originalWeatherData?.end}
                  setHasModifiedWeatherData={modified =>
                    setHasModifiedWeatherDataEvent(modified, 'end')
                  }
                  resetTrigger={resetTrigger}
                  autoWeatherEnabled={autoWeatherEnabled}
                  lastWeatherMode={lastWeatherMode}
                  areDatesInFuture={areDatesInFuture()}
                  apiErrorValue={apiErrorValue}
                />
              </div>
            </FormSection>
          </SectionWrapper>
        </>
      )}
    </div>
  );
};

export default WeatherStep;
